import { fromJS } from "immutable";

import { withMutations } from "@/redux/modules/helpers/reducerWrappers";
import { sumTotalAreaOfSquareMetersInSquareMeters } from "@/utils/measurement/measurement-aggregate";

/**
 * Action type for adding a square metre dimension. Handled by this wrapper so we don't add
 * new logic to the large additional-products.js module.
 */
export const ADD_SQUARE_METER_DIMENSION = "ADD_SQUARE_METER_DIMENSION";
export const DELETE_SQUARE_METER_DIMENSION = "DELETE_SQUARE_METER_DIMENSION";

const getPathConfig = (value) => {
  const { type, itemIndex, cartIndex, subKitIndex } = value;
  if (type === "subKitItem") {
    return {
      basePath: ["items", itemIndex, "subKits", subKitIndex, "kitProducts", cartIndex],
      estimatedKey: "lengthItems",
      hasInitialLengthItems: true,
    };
  }
  if (type === "kitItem") {
    return {
      basePath: ["items", itemIndex, "productKitItems", cartIndex],
      estimatedKey: "lengthItems",
      hasInitialLengthItems: true,
    };
  }
  return {
    basePath: ["items", itemIndex],
    estimatedKey: "subitems",
    hasInitialLengthItems: false,
  };
};

const addSquareMetreDimensionLogic = (_state, value) => {
  const { isActual, amount, length, width, unit } = value;
  const { basePath, estimatedKey, hasInitialLengthItems } = getPathConfig(value);

  const existingItems = _state
    .getIn([...basePath, !isActual ? estimatedKey : "actualLengthItems"], fromJS([]))
    .toJS();
  // `displayUnit` records how the user typed the row; length and width are
  // always canonical metres. initialLengthItems below reuses this same row
  // object. State is camelCase; snakecaseKeys writes it as `display_unit` on save.
  const items = [...existingItems, { amount, length, width, displayUnit: unit }];
  const { total: totalSqm } = sumTotalAreaOfSquareMetersInSquareMeters({ items });
  const immutableItems = fromJS(items);
  const sumObject = fromJS({ total: parseFloat(totalSqm) });

  if (!isActual) {
    _state.setIn([...basePath, estimatedKey], immutableItems);
    if (hasInitialLengthItems) {
      _state.setIn([...basePath, "initialLengthItems"], immutableItems);
    }
    _state.setIn([...basePath, "sumLengths"], sumObject);
  }

  _state.setIn([...basePath, "actualLengthItems"], immutableItems);
  _state.setIn([...basePath, "actualSumLengths"], sumObject);
};

const deleteSquareMetreDimensionLogic = (_state, value) => {
  const { subitemIndex, isActual } = value;
  const { basePath, estimatedKey, hasInitialLengthItems } = getPathConfig(value);

  if (!isActual) {
    _state.deleteIn([...basePath, estimatedKey, subitemIndex]);
    if (hasInitialLengthItems) {
      _state.deleteIn([...basePath, "initialLengthItems", subitemIndex]);
    }
  }

  _state.deleteIn([...basePath, "actualLengthItems", subitemIndex]);

  const items = _state
    .getIn([...basePath, !isActual ? estimatedKey : "actualLengthItems"], fromJS([]))
    .toJS();
  const { total: totalSqm } = sumTotalAreaOfSquareMetersInSquareMeters({ items });
  const sumObject = fromJS({ total: parseFloat(totalSqm) });

  if (!isActual) {
    _state.setIn([...basePath, "sumLengths"], sumObject);
  }

  _state.setIn([...basePath, "actualSumLengths"], sumObject);
};

const addSquareMetreDimensionReducer = withMutations(addSquareMetreDimensionLogic);
const deleteSquareMetreDimensionReducer = withMutations(deleteSquareMetreDimensionLogic);

/**
 * Wraps the original additional-products reducer so that ADD_SQUARE_METER_DIMENSION
 * is handled here instead of in additional-products.js. All other actions are delegated.
 *
 * @param {Function} originalReducer - additionalProducts.reducers
 * @returns {Function} Wrapped reducer for the additionalProducts slice
 */
const createAdditionalProductsWrapper = (originalReducer) => (state, action) => {
  if (action.type === ADD_SQUARE_METER_DIMENSION) {
    return addSquareMetreDimensionReducer(state, action);
  }
  if (action.type === DELETE_SQUARE_METER_DIMENSION) {
    return deleteSquareMetreDimensionReducer(state, action);
  }
  return originalReducer(state, action);
};

export default createAdditionalProductsWrapper;
